// File: frontend/src/pages/InvestorPitch/components/slides/PilotProjectSlide2/PilotProjectSlide2.js

import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { ANIMATION_VARIANTS } from '../../../constants/animations';
import { FiPlayCircle, FiX, FiCheck } from 'react-icons/fi';
import styles from './PilotProjectSlide2.module.css';

const PilotProjectSlide2 = () => {
  const { t } = useLanguage();
  const [activeVideo, setActiveVideo] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [modalVideoError, setModalVideoError] = useState(false);
  const videoRef = useRef(null);

  // Translations
  const translations = {
    'investor_pitch.pilot2.title': 'Improve Commercial Line Underwriting Profitability with AI Agents',
    'investor_pitch.pilot2.video.title': 'Commercial Underwriting AI Demo',
    'investor_pitch.pilot2.watch_demo': 'Watch Demo',
    'investor_pitch.pilot2.goal.title': 'Goal',
    'investor_pitch.pilot2.goal.text': 'Reduce underwriting expenses by 80% while improving risk assessment accuracy by 25%',
    'investor_pitch.pilot2.deliverables.title': 'Deliverables',
    'investor_pitch.pilot2.deliverables.item1': 'AI-powered risk assessment engine for small-to-medium businesses',
    'investor_pitch.pilot2.deliverables.item2': 'Automated document analysis and data extraction from applications',
    'investor_pitch.pilot2.deliverables.item3': 'Integration with third-party data sources for enriched risk profiles',
    'investor_pitch.pilot2.deliverables.item4': 'Rule-based straight-through processing for standard policies',
    'investor_pitch.pilot2.deliverables.item5': 'Underwriter dashboard for exception handling and complex cases',
    'investor_pitch.pilot2.architecture.title': 'Solution Architecture',
    'investor_pitch.pilot2.architecture.text': 'Allegory AI Productivity Suite with dedicated underwriting models, integrated with existing underwriting systems via secure API connections',
    'investor_pitch.pilot2.video_error': 'Video could not be loaded',
    'investor_pitch.pilot2.retry': 'Retry'
  };

  // Pilot project details with video source
  const pilotDetails = {
    video: {
      src: 'https://allegory-public.s3.ca-central-1.amazonaws.com/pitch/pitch-products-ai1.mp4',
      poster: 'https://allegory-public.s3.ca-central-1.amazonaws.com/pitch/pitch-products-ai1-poster.jpg'
    },
    deliverables: [
      t('investor_pitch.pilot2.deliverables.item1'),
      t('investor_pitch.pilot2.deliverables.item2'),
      t('investor_pitch.pilot2.deliverables.item3'),
      t('investor_pitch.pilot2.deliverables.item4'),
      t('investor_pitch.pilot2.deliverables.item5')
    ]
  };

  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handlePlay = () => {
    setActiveVideo({
      title: t('investor_pitch.pilot2.video.title'),
      src: pilotDetails.video.src,
      poster: pilotDetails.video.poster
    });
    setIsModalLoading(true);
    setModalVideoError(false);
  };

  const handleClose = () => {
    setActiveVideo(null);
    setIsModalLoading(false);
    setModalVideoError(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const handleModalVideoLoaded = () => {
    setIsModalLoading(false);
  };

  const handleModalVideoError = () => {
    setModalVideoError(true);
    setIsModalLoading(false);
  };

  // Get video source based on device
  const getVideoSource = (originalSrc) => {
    if (isMobile) {
      // Extract the base filename and add -mobile suffix
      const parts = originalSrc.split('.');
      const extension = parts.pop();
      const basePath = parts.join('.');
      return `${basePath}-mobile.${extension}`;
    }
    return originalSrc;
  };

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && activeVideo) {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [activeVideo]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (activeVideo) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [activeVideo]);

  return (
    <motion.div
      className={styles.container}
      variants={ANIMATION_VARIANTS.stagger.container}
      initial="hidden"
      animate="visible"
    >
      <motion.div 
        className={styles.header}
        variants={ANIMATION_VARIANTS.fadeIn}
      >
        <h2 className={styles.title}>
          {t('investor_pitch.pilot2.title')}
        </h2>
      </motion.div>

      <div className={styles.topContent}>
        <motion.div 
          className={styles.leftSection}
          variants={ANIMATION_VARIANTS.stagger.container}
        >
          <motion.div 
            className={styles.detailCard}
            variants={ANIMATION_VARIANTS.fadeIn}
          >
            <div className={styles.cardHeader}>
              <h3 className={styles.cardTitle}>{t('investor_pitch.pilot2.goal.title')}</h3>
            </div>
            <div className={styles.cardContent}>
              <p className={styles.goalText}>{t('investor_pitch.pilot2.goal.text')}</p>
            </div>
          </motion.div>

          <motion.div 
            className={styles.detailCard}
            variants={ANIMATION_VARIANTS.fadeIn}
          >
            <div className={styles.cardHeader}>
              <h3 className={styles.cardTitle}>{t('investor_pitch.pilot2.architecture.title')}</h3>
            </div>
            <div className={styles.cardContent}>
              <p className={styles.architectureText}>{t('investor_pitch.pilot2.architecture.text')}</p>
            </div>
          </motion.div>
        </motion.div>

        <motion.div 
          className={styles.rightSection}
          variants={ANIMATION_VARIANTS.fadeIn}
        >
          <div className={styles.detailCard}>
            <div className={styles.cardHeader}>
              <h3 className={styles.cardTitle}>{t('investor_pitch.pilot2.deliverables.title')}</h3>
            </div>
            <div className={styles.cardContent}>
              <ul className={styles.deliverablesList}>
                {pilotDetails.deliverables.map((item, index) => (
                  <li key={index} className={styles.deliverableItem}>
                    <FiCheck className={styles.checkIcon} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div 
        className={styles.videoSection}
        variants={ANIMATION_VARIANTS.fadeIn}
      >
        <div 
          className={styles.videoPreview}
          onClick={handlePlay}
        >
          <img 
            src={pilotDetails.video.poster} 
            alt={t('investor_pitch.pilot2.video.title')} 
            className={styles.videoPoster}
          />
          <div className={styles.playIconWrapper}>
            <FiPlayCircle className={styles.playIcon} />
            <span className={styles.playText}>{t('investor_pitch.pilot2.watch_demo')}</span>
          </div>
        </div>
      </motion.div>

      {/* Video Modal */}
      <AnimatePresence>
        {activeVideo && (
          <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          >
            <motion.div
              className={styles.modalContent}
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
            >
              {isModalLoading && (
                <div className={styles.modalLoading}>
                  <div className={styles.loadingSpinner} />
                </div>
              )}
              
              {modalVideoError ? (
                <div className={styles.modalError}>
                  <p>{t('investor_pitch.pilot2.video_error')}</p>
                  <button 
                    className={styles.retryButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      setModalVideoError(false);
                      setIsModalLoading(true);
                      if (videoRef.current) {
                        videoRef.current.load();
                      }
                    }}
                  >
                    {t('investor_pitch.pilot2.retry')}
                  </button>
                </div>
              ) : (
                <video
                  ref={videoRef}
                  className={styles.modalVideo}
                  src={getVideoSource(activeVideo.src)}
                  poster={activeVideo.poster}
                  controls
                  autoPlay
                  onLoadedData={handleModalVideoLoaded}
                  onError={handleModalVideoError}
                />
              )}
              
              <button
                className={styles.closeButton}
                onClick={handleClose}
                aria-label="Close video"
              >
                <FiX />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default PilotProjectSlide2;